import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';
import {
  ListVehicleMaintenanceTypesResponseSchema,
  MaintenanceSourceSchema,
} from '../vehicle/maintenance.schemas.js';
import { GetVehicleUsefulLifeDueResponseSchema } from '../vehicle/useful-life-due.schemas.js';

export const ConsumerMaintenanceStatusSchema = z.enum(['OVERDUE', 'DUE_SOON', 'OK', 'UNKNOWN']);

export const ConsumerMaintenanceItemSchema = z.object({
  id: z.uuid(),
  label: z.string().min(1),
  status: ConsumerMaintenanceStatusSchema,
  lastServiceAtKm: z.number().nullable(),
  lastServiceAtDate: z.string().nullable(),
  dueAtKm: z.number().nullable(),
  dueAtDate: z.string().nullable(),
  remainingKm: z.number().nullable(),
  remainingDays: z.number().nullable(),
  workshopName: z.string().nullable(),
});

export const ConsumerVehicleSourceSchema = z.enum(['PERSONAL', 'WORKSHOP']);

export const ConsumerVehicleItemSchema = z.object({
  source: ConsumerVehicleSourceSchema,
  /** Opaque id for detail/mileage routes (garage id or customerVehicle id). */
  id: z.uuid(),
  consumerGarageVehicleId: z.uuid().nullable(),
  customerVehicleId: z.uuid().nullable(),
  vehicleId: z.uuid(),
  tenantId: z.uuid().nullable(),
  partyId: z.uuid().nullable(),
  workshopName: z.string().nullable(),
  plate: z.string().nullable(),
  brandName: z.string().nullable(),
  modelName: z.string().nullable(),
  manufactureYear: z.number().int().nullable(),
  modelYear: z.number().int().nullable(),
  color: z.string().nullable(),
  chassis: z.string().nullable(),
  currentMileageMeters: z.number().int().nonnegative(),
  overallStatus: ConsumerMaintenanceStatusSchema,
});
export type ConsumerVehicleItem = z.infer<typeof ConsumerVehicleItemSchema>;

/** Consumer-facing list chips (mobile Garagem). */
export const ConsumerVehicleListSourceFilterSchema = z.enum(['ALL', 'WORKSHOP', 'PERSONAL']);
export type ConsumerVehicleListSourceFilter = z.infer<typeof ConsumerVehicleListSourceFilterSchema>;

export const ConsumerVehiclesQuerySchema = PaginationSchema.extend({
  source: ConsumerVehicleListSourceFilterSchema.optional(),
  tenantId: z.uuid().optional(),
  q: z.string().trim().min(1).max(100).optional(),
});
export type ConsumerVehiclesQuery = z.infer<typeof ConsumerVehiclesQuerySchema>;

export const ConsumerVehiclesResponseSchema = z.object({
  items: z.array(ConsumerVehicleItemSchema),
  pagination: CursorPaginationMetaSchema,
});
export type ConsumerVehiclesResponse = z.infer<typeof ConsumerVehiclesResponseSchema>;

export const ConsumerVehicleDetailSchema = ConsumerVehicleItemSchema.extend({
  maintenanceItems: z.array(ConsumerMaintenanceItemSchema),
});
export type ConsumerVehicleDetail = z.infer<typeof ConsumerVehicleDetailSchema>;

/** Consumer-safe maintenance history record (no notes / createdByUserId). */
export const ConsumerMaintenanceHistoryEntryKindSchema = z.enum(['MANUAL', 'USEFUL_LIFE']);
export type ConsumerMaintenanceHistoryEntryKind = z.infer<
  typeof ConsumerMaintenanceHistoryEntryKindSchema
>;

export const ConsumerMaintenanceHistoryItemSchema = z.object({
  id: z.uuid(),
  maintenanceTypeId: z.uuid().nullable(),
  maintenanceTypeName: z.string().min(1),
  performedAtKm: z.number().nonnegative(),
  performedAt: z.string().nullable(),
  source: MaintenanceSourceSchema,
  workshopName: z.string().nullable(),
  tenantId: z.uuid().nullable(),
  entryKind: ConsumerMaintenanceHistoryEntryKindSchema.default('MANUAL'),
});
export type ConsumerMaintenanceHistoryItem = z.infer<typeof ConsumerMaintenanceHistoryItemSchema>;

export const ConsumerMaintenanceHistoryQuerySchema = PaginationSchema;
export type ConsumerMaintenanceHistoryQuery = z.infer<typeof ConsumerMaintenanceHistoryQuerySchema>;

export const ConsumerMaintenanceHistoryResponseSchema = z.object({
  items: z.array(ConsumerMaintenanceHistoryItemSchema),
  pagination: CursorPaginationMetaSchema,
});
export type ConsumerMaintenanceHistoryResponse = z.infer<
  typeof ConsumerMaintenanceHistoryResponseSchema
>;

export const CreateConsumerVehicleBodySchema = z.object({
  /** @deprecated Ignored — personal garage create (ADR-0047). */
  tenantId: z.uuid().optional(),
  vehicleModelId: z.uuid(),
  plate: z.string().min(1),
  color: z.string().nullable().optional(),
  manufactureYear: z.number().int().nullable().optional(),
  modelYear: z.number().int().nullable().optional(),
});
export type CreateConsumerVehicleBody = z.infer<typeof CreateConsumerVehicleBodySchema>;

export const CreateConsumerVehicleResponseSchema = z.object({
  source: z.literal('PERSONAL'),
  id: z.uuid(),
  consumerGarageVehicleId: z.uuid(),
  customerVehicleId: z.uuid().nullable(),
  vehicleId: z.uuid(),
  merged: z.boolean(),
});
export type CreateConsumerVehicleResponse = z.infer<typeof CreateConsumerVehicleResponseSchema>;

export const RecordConsumerVehicleMileageBodySchema = z.object({
  mileageKm: z.number().positive(),
  reason: z.string().trim().min(1).optional(),
});
export type RecordConsumerVehicleMileageBody = z.infer<
  typeof RecordConsumerVehicleMileageBodySchema
>;

export const RecordConsumerVehicleMileageResponseSchema = z.object({
  mileageHistoryId: z.uuid(),
  detail: ConsumerVehicleDetailSchema,
});
export type RecordConsumerVehicleMileageResponse = z.infer<
  typeof RecordConsumerVehicleMileageResponseSchema
>;

export const ConsumerVehicleUsefulLifeDueQuerySchema = z.object({
  apurationKmMeters: z.coerce.number().int().nonnegative().optional(),
  asOf: z.iso.date().optional(),
});
export type ConsumerVehicleUsefulLifeDueQuery = z.infer<
  typeof ConsumerVehicleUsefulLifeDueQuerySchema
>;

export const ConsumerVehicleUsefulLifeDueResponseSchema = GetVehicleUsefulLifeDueResponseSchema;
export type ConsumerVehicleUsefulLifeDueResponse = z.infer<
  typeof ConsumerVehicleUsefulLifeDueResponseSchema
>;

export const RecordConsumerInformedUsefulLifeBodySchema = z.object({
  itemKind: z.enum(['PRODUCT', 'SERVICE']),
  catalogGenericProductId: z.uuid().optional(),
  serviceCanonicalName: z.string().trim().min(1).optional(),
  serviceTypeCanonicalName: z.string().trim().min(1).nullable().optional(),
  displayName: z.string().trim().min(1).max(300),
  performedAtKm: z.number().nonnegative().optional(),
  performedAt: z.iso.date().optional(),
});
export type RecordConsumerInformedUsefulLifeBody = z.infer<
  typeof RecordConsumerInformedUsefulLifeBodySchema
>;

export const RecordConsumerInformedUsefulLifeResponseSchema = z.object({
  factId: z.uuid(),
});
export type RecordConsumerInformedUsefulLifeResponse = z.infer<
  typeof RecordConsumerInformedUsefulLifeResponseSchema
>;

export const RecordConsumerMaintenanceHistoryBodySchema = z.object({
  maintenanceTypeId: z.uuid(),
  performedAtKm: z.number().nonnegative(),
  performedAt: z.iso.date(),
  reason: z.string().trim().min(1).optional(),
});
export type RecordConsumerMaintenanceHistoryBody = z.infer<
  typeof RecordConsumerMaintenanceHistoryBodySchema
>;

export const ConsumerUsefulLifeCatalogItemsQuerySchema = PaginationSchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
});
export type ConsumerUsefulLifeCatalogItemsQuery = z.infer<
  typeof ConsumerUsefulLifeCatalogItemsQuerySchema
>;

export const ConsumerUsefulLifeCatalogItemSchema = z.object({
  id: z.uuid(),
  displayName: z.string().min(1),
  usefulLifeMeters: z.number().int().nullable(),
  usefulLifeMonths: z.number().int().nullable(),
});
export type ConsumerUsefulLifeCatalogItem = z.infer<typeof ConsumerUsefulLifeCatalogItemSchema>;

export const ConsumerUsefulLifeCatalogItemsResponseSchema = z.object({
  items: z.array(ConsumerUsefulLifeCatalogItemSchema),
  pagination: CursorPaginationMetaSchema,
});
export type ConsumerUsefulLifeCatalogItemsResponse = z.infer<
  typeof ConsumerUsefulLifeCatalogItemsResponseSchema
>;

export const ConsumerMaintenanceTypesResponseSchema = ListVehicleMaintenanceTypesResponseSchema;
export type ConsumerMaintenanceTypesResponse = z.infer<
  typeof ConsumerMaintenanceTypesResponseSchema
>;
